import { Fragment, useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { CopyButton } from "~/components/copy-button";
import { cn } from "~/lib/cn";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  AdminApiError,
  fetchAdminStatus,
  skipStockHeldCycles,
} from "~/lib/admin-api";
import {
  dateDisplayProps,
  formatBillingStatus,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatGrams,
  formatHoldStatus,
  formatKind,
  formatMailStatus,
  formatMailTarget,
  formatMillingType,
  formatMonth,
  formatNumber,
  formatOrderStatus,
  formatVariety,
  isPast,
  mailTarget,
  shortId,
} from "~/lib/format";
import {
  cycleSkipReasonFilters,
  cycleStatusBadgeVariant,
  cycleStatusFilters,
  formatCycleSkipReason,
  formatCycleStatus,
} from "~/lib/subscription-cycles";
import type { CyclesStatus, CycleStatus } from "~/types/admin-cycles";
import type {
  AdminStatusResponse,
  CampaignQuota,
  CapacityStatus,
  HoldsStatus,
  MailOutboxSummary,
  OrdersStatus,
  StockHoldsStatus,
  StockHoldSkipResponse,
  StockHoldRow,
  StockPool,
} from "~/types/admin-status";

const orderLabels = {
  reserved: "予約",
  allocated: "割当済み",
  canceled: "キャンセル",
  returned: "返品",
} as const;

const holdLabels = {
  holding: "保持中",
  paymentInProgress: "決済中",
  confirmed: "確定",
  released: "解放",
  expired: "期限切れ",
} as const;

const mailLabels = {
  pending: "待機",
  sending: "送信中",
  sent: "送信済み",
  suppressed: "抑止",
  dead: "dead",
} as const;

// ラベルの正は lib/subscription-cycles.ts に一本化する。
// 一覧タブと状況ボードで同じ語彙（returned = 「未受取終了」等）を保証するため、
// ここで literal を再掲しない。
const cycleLabels = Object.fromEntries(
  cycleStatusFilters.map((status) => [status, formatCycleStatus(status)]),
) as Readonly<Record<CycleStatus, string>>;

type BadgeVariant =
  "default" | "secondary" | "destructive" | "warning" | "outline";

function statusVariant(status: string): BadgeVariant {
  if (status === "dead" || status === "expired" || status === "returned") {
    return "destructive";
  }
  if (
    status === "pending" ||
    status === "sending" ||
    status === "holding" ||
    status === "payment_in_progress"
  ) {
    return "warning";
  }
  if (status === "sent" || status === "confirmed" || status === "allocated") {
    return "default";
  }
  return "secondary";
}

function StatusCounts<T extends string>({
  counts,
  labels,
  variantForStatus = statusVariant,
}: {
  counts: Record<T, number>;
  labels: Readonly<Record<T, string>>;
  variantForStatus?: (status: string) => BadgeVariant;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {(Object.keys(labels) as T[]).map((status) => (
        <Badge key={status} variant={variantForStatus(status)}>
          {labels[status]}{" "}
          <span className="tabular-nums">{counts[status]}</span>
        </Badge>
      ))}
    </div>
  );
}

function EmptyRow({ columns, label }: { columns: number; label: string }) {
  return (
    <TableRow>
      <TableCell
        colSpan={columns}
        className="h-20 text-center text-muted-foreground"
      >
        {label}
      </TableCell>
    </TableRow>
  );
}

function OrdersPanel({
  orders,
  className,
}: {
  orders: OrdersStatus;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>注文</CardTitle>
        <CardDescription>直近20件とステータス別件数</CardDescription>
        <CardAction>
          <StatusCounts counts={orders.countsByStatus} labels={orderLabels} />
        </CardAction>
      </CardHeader>
      <CardContent className="overflow-x-auto px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">作成日時</TableHead>
              <TableHead>商品</TableHead>
              <TableHead className="text-right">金額</TableHead>
              <TableHead>状態</TableHead>
              <TableHead className="pr-6">配送</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.recent.length === 0 ? (
              <EmptyRow columns={5} label="注文はありません。" />
            ) : (
              orders.recent.map((order) => {
                const promisedMonth = dateDisplayProps(
                  order.promisedMonth,
                  formatMonth,
                );
                const deliveryWindow =
                  order.deliveryWindowStart || order.deliveryWindowEnd ? (
                    <>
                      <span {...dateDisplayProps(order.deliveryWindowStart, formatDate)} />
                      {" – "}
                      <span {...dateDisplayProps(order.deliveryWindowEnd, formatDate)} />
                    </>
                  ) : (
                    "—"
                  );
                return (
                  <Fragment key={order.id}>
                    <TableRow className="peer border-b-0 [&:has(+_tr:hover)]:bg-muted/50">
                      <TableCell
                        className="whitespace-nowrap pl-6 text-sm"
                        {...dateDisplayProps(order.createdAt, formatDateTime)}
                      />
                      <TableCell className="whitespace-nowrap text-sm">
                        <span className="mr-2">{formatKind(order.kind)}</span>
                        {formatVariety(order.variety)} /{" "}
                        {formatMillingType(order.millingType)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                        {formatCurrency(order.amount)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <span className="inline-flex flex-wrap items-center gap-1">
                          <Badge variant={statusVariant(order.status)} title={order.status}>
                            {formatOrderStatus(order.status)}
                          </Badge>
                          <Badge
                            variant={statusVariant(order.billingStatus)}
                            title={order.billingStatus}
                          >
                            {formatBillingStatus(order.billingStatus)}
                          </Badge>
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap pr-6 text-sm">
                        {deliveryWindow}
                        {promisedMonth.children && promisedMonth.children !== "—" ? (
                          <span className="ml-1 text-muted-foreground">
                            （{promisedMonth.children}お届け・予約）
                          </span>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    <TableRow className="peer-hover:bg-muted/50">
                      <TableCell
                        colSpan={5}
                        className="pl-6 pt-0 font-mono text-xs text-muted-foreground"
                      >
                        <span className="inline-flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="inline-flex items-center gap-1">
                            注文 ID {order.id}
                            <CopyButton value={order.id} />
                          </span>
                          <span className="inline-flex items-center gap-1">
                            決済 ID {order.payment_ref ?? "—"}
                            {order.payment_ref ? (
                              <CopyButton value={order.payment_ref} />
                            ) : null}
                          </span>
                        </span>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function HoldsPanel({ holds, className }: { holds: HoldsStatus; className?: string }) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>仮確保</CardTitle>
        <CardDescription>保持中・決済中の全件</CardDescription>
        <CardAction>
          <StatusCounts counts={holds.countsByStatus} labels={holdLabels} />
        </CardAction>
      </CardHeader>
      <CardContent className="overflow-x-auto px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">期限</TableHead>
              <TableHead>キャンペーン / 月</TableHead>
              <TableHead className="text-right">金額</TableHead>
              <TableHead>状態</TableHead>
              <TableHead>決済開始</TableHead>
              <TableHead className="pr-6">作成日時</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {holds.active.length === 0 ? (
              <EmptyRow columns={6} label="active hold はありません。" />
            ) : (
              holds.active.map((hold) => {
                const expired = isPast(hold.expiresAt);
                const expiredRowClass = expired
                  ? "bg-destructive/10 hover:bg-destructive/15"
                  : undefined;
                return (
                  <Fragment key={hold.id}>
                    <TableRow
                      className={cn(
                        "peer border-b-0 [&:has(+_tr:hover)]:bg-muted/50",
                        expiredRowClass,
                      )}
                    >
                      <TableCell className="whitespace-nowrap pl-6 text-sm">
                        <span
                          className={
                            expired ? "font-medium text-red-700" : undefined
                          }
                          {...dateDisplayProps(hold.expiresAt, formatDateTime)}
                        />{" "}
                        {expired ? (
                          <Badge variant="destructive">期限超過</Badge>
                        ) : null}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <span className="font-mono" title={hold.campaignId}>
                          {shortId(hold.campaignId)}
                        </span>{" "}
                        / <span {...dateDisplayProps(hold.month, formatMonth)} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-sm tabular-nums">
                        {formatCurrency(hold.amount)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <Badge variant={statusVariant(hold.status)} title={hold.status}>
                          {formatHoldStatus(hold.status)}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className="whitespace-nowrap text-sm"
                        {...dateDisplayProps(hold.paymentStartedAt, formatDateTime)}
                      />
                      <TableCell
                        className="whitespace-nowrap pr-6 text-sm"
                        {...dateDisplayProps(hold.createdAt, formatDateTime)}
                      />
                    </TableRow>
                    <TableRow className={cn("peer-hover:bg-muted/50", expiredRowClass)}>
                      <TableCell
                        colSpan={6}
                        className="pl-6 pt-0 font-mono text-xs text-muted-foreground"
                      >
                        <span className="inline-flex items-center gap-1">
                          hold ID {hold.id}
                          <CopyButton value={hold.id} />
                        </span>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function stockHoldSkipErrorMessage(error: Error | null): string | null {
  if (!error) return null;

  if (error instanceof AdminApiError) {
    if (error.status === 401 || error.status === 403) {
      return "管理者権限がありません。";
    }

    switch (error.code) {
      case "cycle_not_found":
        return "対象の回が見つかりません。状況を更新してください。";
      case "not_stock_held":
        return "在庫不足保留ではない回が含まれています。";
      case "already_billed":
        return "課金済みまたは課金中の回が含まれています。";
      case "cycle_state_conflict":
      case "out_of_stock":
      case "allocation_overflow":
        return "帳簿を確認してください。";
    }
  }

  return "スキップ確定に失敗しました。";
}

function selectionSummary(rows: StockHoldRow[]) {
  return {
    varieties: [...new Set(rows.map((row) => formatVariety(row.variety)))],
    weeks: [...new Set(rows.map((row) => formatDate(row.target_iso_week)))],
  };
}

function StockHoldsPanel({
  stockHolds,
  accessToken,
  onSkipped,
}: {
  stockHolds: StockHoldsStatus;
  accessToken: string;
  onSkipped: () => Promise<void>;
}) {
  const active = stockHolds.count > 0;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState<StockHoldSkipResponse | null>(
    null,
  );
  const visibleIds = stockHolds.rows.map((hold) => hold.cycle_id);
  const selectedRows = stockHolds.rows.filter((hold) =>
    selectedIds.has(hold.cycle_id),
  );
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((cycleId) => selectedIds.has(cycleId));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const summary = selectionSummary(selectedRows);
  const skipMutation = useMutation({
    mutationFn: (cycleIds: string[]) =>
      skipStockHeldCycles(accessToken, { cycle_ids: cycleIds }),
    onSuccess: async (result) => {
      setConfirmOpen(false);
      setSelectedIds(new Set());
      setLastResult(result);
      await onSkipped();
    },
  });

  useEffect(() => {
    const visible = new Set(visibleIds);
    setSelectedIds((current) => {
      const next = new Set(
        [...current].filter((cycleId) => visible.has(cycleId)),
      );
      return next.size === current.size ? current : next;
    });
  }, [stockHolds.rows]);

  function toggleCycle(cycleId: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(cycleId)) {
        next.delete(cycleId);
      } else {
        next.add(cycleId);
      }
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  }

  function openConfirmation() {
    if (selectedRows.length === 0) return;
    skipMutation.reset();
    setLastResult(null);
    setConfirmOpen(true);
  }

  const mutationError = stockHoldSkipErrorMessage(skipMutation.error);

  return (
    <Card
      className={
        active
          ? "rounded-none border-amber-300 bg-amber-50 shadow-none"
          : "rounded-none border-zinc-300 bg-white shadow-none"
      }
    >
      <CardHeader>
        <CardTitle className={active ? "text-amber-700" : undefined}>
          在庫不足保留
        </CardTitle>
        <CardAction>
          <span
            className={
              active
                ? "border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                : "border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600"
            }
          >
            保留 <span className="tabular-nums">{stockHolds.count}</span>
          </span>
        </CardAction>
      </CardHeader>
      <CardContent className="px-0">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-300 px-6 py-3">
          <span className="text-sm text-zinc-600">
            選択 <span className="tabular-nums">{selectedRows.length}</span>件
          </span>
          <Button
            className="rounded-none border border-amber-500 bg-amber-500 text-stone-950 shadow-none hover:bg-amber-400 focus-visible:border-amber-500 focus-visible:ring-amber-500/40"
            onClick={openConfirmation}
            disabled={selectedRows.length === 0 || skipMutation.isPending}
          >
            選択した回をスキップ確定
          </Button>
        </div>
        {lastResult ? (
          <div
            className="border-b border-amber-300 bg-amber-50 px-6 py-3 text-sm text-amber-800"
            role="status"
          >
            スキップ {lastResult.skipped}件 / noop {lastResult.noop}件
          </div>
        ) : null}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 pl-6">
                <input
                  type="checkbox"
                  className="size-4 accent-amber-500 outline-amber-500"
                  checked={allSelected}
                  ref={(element) => {
                    if (element) element.indeterminate = someSelected;
                  }}
                  onChange={toggleAll}
                  aria-label="全選択"
                />
              </TableHead>
              <TableHead>検知時刻</TableHead>
              <TableHead>契約ID</TableHead>
              <TableHead>品種 / 精米</TableHead>
              <TableHead>対象週</TableHead>
              <TableHead className="pr-6">カットオフ</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stockHolds.rows.length === 0 ? (
              <EmptyRow columns={6} label="在庫不足保留はありません。" />
            ) : (
              stockHolds.rows.map((hold) => (
                <TableRow
                  key={hold.cycle_id}
                  className="bg-amber-50 hover:bg-amber-100"
                >
                  <TableCell className="pl-6">
                    <input
                      type="checkbox"
                      className="size-4 accent-amber-500 outline-amber-500"
                      checked={selectedIds.has(hold.cycle_id)}
                      onChange={() => toggleCycle(hold.cycle_id)}
                      aria-label={`${hold.subscription_short_id} ${formatDate(hold.target_iso_week)} を選択`}
                    />
                  </TableCell>
                  <TableCell
                    className="text-amber-700"
                    {...dateDisplayProps(hold.detected_at, formatDateTime)}
                  />
                  <TableCell className="font-mono">
                    {hold.subscription_short_id}
                  </TableCell>
                  <TableCell>
                    {formatVariety(hold.variety)} /{" "}
                    {formatMillingType(hold.milling_type)}
                  </TableCell>
                  <TableCell {...dateDisplayProps(hold.target_iso_week, formatDate)} />
                  <TableCell
                    className="pr-6"
                    {...dateDisplayProps(hold.cutoff_at, formatDateTime)}
                  />
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open && !skipMutation.isPending) setConfirmOpen(false);
        }}
      >
        <DialogContent
          className="rounded-none border-amber-300 bg-white text-zinc-900 shadow-none"
          overlayClassName="bg-black/50 backdrop-blur-none"
          showCloseButton={!skipMutation.isPending}
        >
          <DialogHeader>
            <DialogTitle className="text-zinc-900">スキップ確定</DialogTitle>
            <DialogDescription className="text-zinc-500">
              選択した回をスキップし、在庫を解放します。
            </DialogDescription>
          </DialogHeader>
          <dl className="grid grid-cols-[5rem_1fr] gap-x-4 gap-y-2 border-y border-zinc-300 px-6 py-4 text-sm">
            <dt className="text-zinc-500">件数</dt>
            <dd className="text-zinc-900">{selectedRows.length}件</dd>
            <dt className="text-zinc-500">品種</dt>
            <dd className="text-zinc-900">{summary.varieties.join("、")}</dd>
            <dt className="text-zinc-500">対象週</dt>
            <dd className="text-zinc-900">{summary.weeks.join("、")}</dd>
          </dl>
          {mutationError ? (
            <div
              className="mx-6 border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              role="alert"
            >
              <p className="font-semibold">全件失敗</p>
              <p className="mt-1">{mutationError}</p>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-none border-zinc-300 bg-white text-zinc-900 shadow-none hover:bg-zinc-100 focus-visible:border-amber-500 focus-visible:ring-amber-500/40"
              onClick={() => setConfirmOpen(false)}
              disabled={skipMutation.isPending}
            >
              戻る
            </Button>
            <Button
              className="rounded-none bg-amber-500 text-stone-950 shadow-none hover:bg-amber-400 focus-visible:border-amber-500 focus-visible:ring-amber-500/40"
              onClick={() =>
                skipMutation.mutate(selectedRows.map((hold) => hold.cycle_id))
              }
              disabled={skipMutation.isPending || selectedRows.length === 0}
            >
              {skipMutation.isPending ? "実行中…" : "スキップ確定"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function SubscriptionCyclesPanel({ cycles, className }: { cycles: CyclesStatus; className?: string }) {
  const visibleSkipReasons = cycleSkipReasonFilters.filter(
    (reason) => cycles.skipCountsByReason[reason] > 0,
  );

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>定期便の回</CardTitle>
        <CardDescription>状態別件数と直近のスキップ10件</CardDescription>
        <CardAction>
          <StatusCounts
            counts={cycles.countsByStatus}
            labels={cycleLabels}
            variantForStatus={cycleStatusBadgeVariant}
          />
        </CardAction>
      </CardHeader>
      <CardContent className="px-0">
        {visibleSkipReasons.length > 0 ? (
          <div className="flex flex-wrap gap-2 border-b px-6 pb-4">
            {visibleSkipReasons.map((reason) => (
              <Badge key={reason} variant="warning">
                {formatCycleSkipReason(reason)}{" "}
                <span className="tabular-nums">
                  {cycles.skipCountsByReason[reason]}
                </span>
              </Badge>
            ))}
          </div>
        ) : null}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">対象週</TableHead>
              <TableHead>品種 / 精米</TableHead>
              <TableHead>請求</TableHead>
              <TableHead>配送枠</TableHead>
              <TableHead className="pr-6">更新</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cycles.recentSkipped.length === 0 ? (
              <EmptyRow columns={5} label="スキップされた回はありません。" />
            ) : (
              cycles.recentSkipped.map((cycle) => (
                <Fragment key={cycle.id}>
                  <TableRow className="peer whitespace-nowrap border-b-0 [&:has(+_tr:hover)]:bg-muted/50">
                    <TableCell
                      className="pl-6"
                      {...dateDisplayProps(cycle.target_iso_week, formatDate)}
                    />
                    <TableCell>
                      {formatVariety(cycle.variety)} /{" "}
                      {formatMillingType(cycle.milling_type)}
                    </TableCell>
                    <TableCell>
                      <Badge title={cycle.billing_status} variant="secondary">
                        {formatBillingStatus(cycle.billing_status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span
                        {...dateDisplayProps(
                          cycle.delivery_window_start,
                          formatDate,
                        )}
                      />
                      {" – "}
                      <span
                        {...dateDisplayProps(
                          cycle.delivery_window_end,
                          formatDate,
                        )}
                      />
                    </TableCell>
                    <TableCell
                      className="pr-6"
                      {...dateDisplayProps(cycle.updated_at, formatDateTime)}
                    />
                  </TableRow>
                  <TableRow className="peer-hover:bg-muted/50">
                    <TableCell
                      colSpan={5}
                      className="pl-6 pt-0 text-xs text-muted-foreground"
                    >
                      <span className="inline-flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span className="inline-flex items-center gap-1 font-mono">
                          契約 ID {cycle.subscription_id}
                          <CopyButton value={cycle.subscription_id} />
                        </span>
                        <span className="inline-flex items-center gap-1 font-mono">
                          回 ID {cycle.id}
                          <CopyButton value={cycle.id} />
                        </span>
                        <Badge
                          variant={cycle.skip_reason ? "warning" : "outline"}
                          title={cycle.skip_reason ?? undefined}
                        >
                          {formatCycleSkipReason(cycle.skip_reason)}
                        </Badge>
                      </span>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function QuotaMeter({ quota }: { quota: CampaignQuota }) {
  const used = quota.held + quota.consumed;
  const remaining = Math.max(0, quota.capacity - used);
  const percentage =
    quota.capacity > 0 ? Math.min(100, (used / quota.capacity) * 100) : 0;

  return (
    <div className="grid gap-3 rounded-lg border bg-muted/20 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p
            className="font-medium"
            {...dateDisplayProps(quota.month, formatMonth)}
          />
          <p
            className="font-mono text-xs text-muted-foreground"
            title={quota.campaignId}
          >
            {shortId(quota.campaignId)}
          </p>
        </div>
        <p className="text-right text-sm">
          残り{" "}
          <span className="font-semibold tabular-nums">
            {formatNumber(remaining)}
          </span>
        </p>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-secondary"
        role="progressbar"
        aria-label={`${quota.month} の使用枠`}
        aria-valuemin={0}
        aria-valuemax={quota.capacity}
        aria-valuenow={used}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width]"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
        <span>総枠 {formatNumber(quota.capacity)}</span>
        <span>仮確保 {formatNumber(quota.held)}</span>
        <span>消費 {formatNumber(quota.consumed)}</span>
      </div>
    </div>
  );
}

function StockMeter({ pool }: { pool: StockPool }) {
  const remaining = Math.max(
    0,
    pool.capacityBrownRiceG - pool.reservedBrownRiceG,
  );
  const percentage =
    pool.capacityBrownRiceG > 0
      ? Math.min(100, (pool.reservedBrownRiceG / pool.capacityBrownRiceG) * 100)
      : 0;

  return (
    <div className="grid gap-3 rounded-lg border bg-muted/20 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">
              {formatVariety(pool.variety)}・{pool.cropYear}年産
            </p>
            <Badge variant={pool.acceptingNew ? "default" : "secondary"}>
              {pool.acceptingNew ? "受付中" : "受付停止"}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">玄米換算</p>
        </div>
        <p className="text-right text-sm">
          残り{" "}
          <span className="font-semibold tabular-nums">
            {formatGrams(remaining)}
          </span>
        </p>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-secondary"
        role="progressbar"
        aria-label={`${pool.variety} ${pool.cropYear}年産の予約在庫`}
        aria-valuemin={0}
        aria-valuemax={pool.capacityBrownRiceG}
        aria-valuenow={pool.reservedBrownRiceG}
      >
        <div
          className="h-full rounded-full bg-primary transition-[width]"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
        <span>容量 {formatGrams(pool.capacityBrownRiceG)}</span>
        <span>予約済み {formatGrams(pool.reservedBrownRiceG)}</span>
      </div>
    </div>
  );
}

function CapacityPanel({ capacity }: { capacity: CapacityStatus }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>枠 / 在庫</CardTitle>
        <CardDescription>月次枠と品種別年産在庫（玄米換算）</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-6">
        <section className="grid gap-3">
          <h3 className="text-sm font-medium">月次枠</h3>
          {capacity.campaignQuotas.length === 0 ? (
            <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              枠データはありません。
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {capacity.campaignQuotas.map((quota) => (
                <QuotaMeter
                  key={`${quota.campaignId}:${quota.month}`}
                  quota={quota}
                />
              ))}
            </div>
          )}
        </section>
        <section className="grid gap-3">
          <h3 className="text-sm font-medium">品種別年産在庫</h3>
          {capacity.stockPools.length === 0 ? (
            <p className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
              在庫データはありません。
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {capacity.stockPools.map((pool) => (
                <StockMeter
                  key={`${pool.variety}:${pool.cropYear}`}
                  pool={pool}
                />
              ))}
            </div>
          )}
        </section>
      </CardContent>
    </Card>
  );
}

function MailOutboxPanel({ mailOutbox, className }: { mailOutbox: MailOutboxSummary; className?: string }) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>送信メール</CardTitle>
        <CardDescription>
          対応が必要な待機・送信中・dead の直近20件
        </CardDescription>
        <CardAction>
          <StatusCounts
            counts={mailOutbox.countsByStatus}
            labels={mailLabels}
          />
        </CardAction>
      </CardHeader>
      <CardContent className="px-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">作成日時</TableHead>
              <TableHead>メール種別</TableHead>
              <TableHead>宛先</TableHead>
              <TableHead>対象</TableHead>
              <TableHead>状態</TableHead>
              <TableHead className="text-right">試行</TableHead>
              <TableHead>次回試行</TableHead>
              <TableHead className="pr-6">最終エラー</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mailOutbox.attention.length === 0 ? (
              <EmptyRow columns={8} label="対応が必要なメールはありません。" />
            ) : (
              mailOutbox.attention.map((mail) => (
                <TableRow
                  key={mail.id}
                  className={
                    mail.status === "dead"
                      ? "bg-destructive/10 hover:bg-destructive/15"
                      : undefined
                  }
                >
                  <TableCell
                    className="pl-6"
                    {...dateDisplayProps(mail.createdAt, formatDateTime)}
                  />
                  <TableCell>{mail.topic}</TableCell>
                  <TableCell>{mail.recipient}</TableCell>
                  <TableCell
                    className="text-muted-foreground"
                    title={mailTarget(mail)?.id ?? undefined}
                  >
                    {formatMailTarget(mail)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(mail.status)} title={mail.status}>
                      {formatMailStatus(mail.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {mail.attemptCount}
                  </TableCell>
                  <TableCell {...dateDisplayProps(mail.nextAttemptAt, formatDateTime)} />
                  <TableCell className="max-w-80 whitespace-normal text-muted-foreground">
                    {mail.lastError ?? "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function BoardPanels({
  data,
  accessToken,
  onStockHoldsSkipped,
}: {
  data: AdminStatusResponse;
  accessToken: string;
  onStockHoldsSkipped: () => Promise<void>;
}) {
  return (
    // 並び = 運用で見る順（注文 → 定期回 → 枠/在庫 → 在庫仮確保 → 仮確保 → メール）。全パネル全幅・1 カラム（2026-08-30 9232さん判断）
    <div className="grid gap-6">
      <OrdersPanel orders={data.orders} />
      {data.cycles ? <SubscriptionCyclesPanel cycles={data.cycles} /> : null}
      <CapacityPanel capacity={data.capacity} />
      <StockHoldsPanel
        stockHolds={data.stock_holds}
        accessToken={accessToken}
        onSkipped={onStockHoldsSkipped}
      />
      <HoldsPanel holds={data.holds} />
      <MailOutboxPanel mailOutbox={data.mailOutbox} />
    </div>
  );
}

export function StatusBoard({
  accessToken,
  userId,
}: {
  accessToken: string;
  userId: string;
}) {
  const query = useQuery({
    queryKey: ["admin-status", userId],
    queryFn: ({ signal }) => fetchAdminStatus(accessToken, signal),
    // ポーリングはしない。タブに戻った時（staleTime 超過後）と手動ボタンで再取得する。
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      if (
        error instanceof AdminApiError &&
        error.status >= 400 &&
        error.status < 500
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  if (query.error instanceof AdminApiError && query.error.status === 403) {
    return (
      <Alert variant="destructive" className="mx-auto max-w-2xl">
        <AlertTitle>権限がありません</AlertTitle>
        <AlertDescription>
          このアカウントに管理者権限が付与されていません。閲覧が必要な場合は管理者に権限の付与を依頼してください。
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">状況ボード</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {query.data ? (
              <>
                <span
                  {...dateDisplayProps(query.data.generatedAt, formatDateTime)}
                />
                {" 時点のデータ"}
              </>
            ) : ""}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          {query.isFetching ? "更新中…" : "今すぐ更新"}
        </Button>
      </div>

      {query.error && !query.data ? (
        <Alert variant="destructive">
          <AlertTitle>状況を取得できませんでした</AlertTitle>
          <AlertDescription>{query.error.message}</AlertDescription>
        </Alert>
      ) : null}

      {query.error && query.data ? (
        <Alert variant="warning">
          <AlertTitle>最新データへの更新に失敗しました</AlertTitle>
          <AlertDescription>
            前回取得した内容を表示しています。{query.error.message}
          </AlertDescription>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="grid min-h-80 place-items-center rounded-xl border bg-card text-sm text-muted-foreground">
          状況を読み込んでいます…
        </div>
      ) : null}

      {query.data ? (
        <BoardPanels
          data={query.data}
          accessToken={accessToken}
          onStockHoldsSkipped={async () => {
            await query.refetch();
          }}
        />
      ) : null}
    </div>
  );
}
